import {
  DisplayableShareLandTransaction,
  Land,
  displayableTransactionTypes,
  isEqualLowerCase,
} from '@shareland/shareland-common';
import {
  CollateralPosition__factory,
  LiquidityProviderToken__factory,
  ShareLandMarket__factory,
} from '@shareland/shareland-contracts';
import {Hex, Log, getAbiItem} from 'viem';

const removeDuplicates = (transactions: DisplayableShareLandTransaction[]): DisplayableShareLandTransaction[] => {
  const unique: DisplayableShareLandTransaction[] = [];

  transactions.forEach(elem => {
    if (!unique.find(val => val.transactionHash === elem.transactionHash)) {
      unique.push(elem);
    }
  });
  return unique;
};

const filterByLand = (transactions: DisplayableShareLandTransaction[], land: Land): DisplayableShareLandTransaction[] =>
  transactions?.filter(elem => elem.landId === land.id);

const sortByLatestBlockNumber = (transactions: DisplayableShareLandTransaction[]) =>
  transactions.sort((a, b) => b.blockNumber - a.blockNumber);

export const transactionAbiEvents = {
  marketTransaction: getAbiItem({
    abi: ShareLandMarket__factory.abi,
    name: 'MarketTransaction',
  } as const),
  collateralPositionTransaction: getAbiItem({
    abi: CollateralPosition__factory.abi,
    name: 'CollateralPositionTransaction',
  } as const),
  liquidityProviderTokenTransaction: getAbiItem({
    abi: LiquidityProviderToken__factory.abi,
    name: 'LiquidityProviderTokenTransaction',
  }),
};

type MarketEventLogArgs = {
  account: Hex;
  transactionType: number;
  landTokenAmount: bigint;
  landTokenBalance: bigint;
  stableTokenAmount: bigint;
  lpTokenAmount?: bigint;
  transactionFees?: bigint;
  marketPrice?: bigint;
};

export type MarketEventLog = Log<
  bigint,
  number,
  false,
  | typeof transactionAbiEvents.marketTransaction
  | typeof transactionAbiEvents.collateralPositionTransaction
  | typeof transactionAbiEvents.liquidityProviderTokenTransaction
>;

const eventLogToShareLandTransaction = (
  eventLog: MarketEventLog,
  lands: Land[],
): DisplayableShareLandTransaction | null => {
  if (!eventLog.transactionHash || !eventLog.blockNumber) return null;

  const land = lands.find(({contractAddresses}) =>
    Object.values(contractAddresses).some(contractAddress =>
      isEqualLowerCase(contractAddress as string, eventLog.address),
    ),
  );
  if (!land) return null;

  const args = eventLog.args as MarketEventLogArgs;

  if (
    Object.values(land.contractAddresses).some(contractAddress =>
      isEqualLowerCase(contractAddress as string, args.account),
    ) ||
    !displayableTransactionTypes.includes(args.transactionType)
  )
    return null;

  return {
    landId: land.id,
    blockNumber: Number(eventLog.blockNumber),
    transactionHash: eventLog.transactionHash,
    account: args.account,
    type: args.transactionType,
    raw: {
      landTokenAmount: Number(args.landTokenAmount),
      stableTokenAmount: Number(args.stableTokenAmount),
      ...(args.lpTokenAmount !== undefined && {lpTokenAmount: Number(args.lpTokenAmount)}),
      ...(args.transactionFees !== undefined && {
        transactionFees: Number(args.transactionFees),
      }),
    },
  };
};

export const transactionUtils = {
  removeDuplicates,
  filterByLand,
  sortByLatestBlockNumber,
  eventLogToShareLandTransaction,
};
